import {
  ApiOutlined,
  CalendarOutlined,
  GlobalOutlined,
  LogoutOutlined,
  PicCenterOutlined,
  SafetyOutlined,
  SwitcherOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Dropdown } from 'antd'
import Link from 'next/link'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store'
import styled from 'styled-components'
import { useCompanyQuery } from '../graphql/query/company.query'
import { useChangeLanguage } from '../hooks/use.change.language'
import { usePermission } from '../hooks/use.permission'
import { useProfile } from '../hooks/use.profile'
import { useSnippetHint } from '../hooks/use.snippet.hint'
import { useWindowSize } from '../hooks/use.window.size'
import { getPublicEnv } from '@tucan/utils'
import { getBoolean } from '../utils/get.boolean'
import { removeFalsy } from '../utils/remove.falsy'
import { captureException } from '@tucan/utils/error-tracking'
import { UserAvatar } from './user.avatar'

const Trigger = styled.div`
  cursor: pointer;
  font-size: 18px;
  line-height: 64px;
  white-space: nowrap;

  .ant-avatar {
    margin-right: 8px;
  }

  @media (max-width: 600px) {
    .text {
      display: none;
    }
  }
`

interface Props {
  companyName: string
}

export const ToolbarUserMenu: React.FC<Props> = (props) => {
  const { t, i18n } = useTranslation()
  const { profile } = useProfile()
  const { isStaff } = usePermission()
  const { visible: showSnippetHint, toggle: toggleSnippetHint } = useSnippetHint()
  const { width } = useWindowSize()
  const isMobile = width < 600
  const companyId = useAppSelector(state => state.company.id)

  const { data: companyData } = useCompanyQuery({
    skip: !companyId,
    variables: {
      id: String(companyId),
    },
  })

  const apiKeysEnabled = companyData?.company?.features?.includes('api') ?? false

  const changeLanguage = useChangeLanguage()

  const setLanguage = useCallback((lang: string) => {
    changeLanguage(lang).catch(e => {
      captureException(e)
    })
  }, [changeLanguage])

  if (!profile) {
    return null
  }

  const lang = `${i18n.language}`.split('-').shift() || 'de'
  const avatar = <UserAvatar user={profile} />

  const inhouse = getBoolean(getPublicEnv('INHOUSE'))

  return (
    <Dropdown
      menu={{
        items: removeFalsy([
          {
            icon: <UserOutlined />,
            key: 'profile',
            label: (
              <Link href={`/${i18n.language}/profile`}>
                {t('user-account', 'Account')}
              </Link>
            ),
          },
          apiKeysEnabled && {
            icon: <ApiOutlined />,
            key: 'api-keys',
            label: (
              <Link href={`/${i18n.language}/profile/api`}>
                {t('user-api-keys', 'API Keys')}
              </Link>
            ),
          },
          isStaff && {
            icon: <PicCenterOutlined />,
            key: 'snippet-toggle',
            label: showSnippetHint ? t('hide-snippet-hints', 'Hide Snippet Hints') : t('show-snippet-hints', 'Show Snippet Hints'),
            onClick: toggleSnippetHint,
          },
          !inhouse && {
            icon: <SafetyOutlined />,
            key: 'privacy',
            label: (
              <a href={getPublicEnv('PRIVACY_AGREEMENT_URL')}>
                {t('user-privacy', 'Privacy')}
              </a>
            ),
          },
          !inhouse && {
            icon: <CalendarOutlined />,
            key: 'calendar',
            label: (
              <Link href={`/${i18n.language}/profile/calendar`}>
                {t('personal-calendar', 'Personal calendar')}
              </Link>
            ),
          },
          isMobile && {
            icon: <SwitcherOutlined />,
            key: 'choose-company',
            label: (
              <Link href={`/${i18n.language}/app/company/choose`}>
                {props.companyName}
              </Link>
            ),
          },
          {
            children: removeFalsy([
              {
                key: 'en',
                label: 'English',
                onClick: () => setLanguage('en'),
              },
              {
                key: 'de',
                label: 'Deutsch',
                onClick: () => setLanguage('de'),
              },
              (profile?.isStaff || lang === 'cimode') && {
                key: 'cimode',
                label: 'TRANSLATION KEYS',
                onClick: () => setLanguage('cimode'),
              },
            ]),
            icon: <GlobalOutlined />,
            key: 'lang',
            label: t('change-language', 'Change Language'),
          },
          {
            key: 'pre-logout',
            type: 'divider',
          },
          {
            icon: <LogoutOutlined />,
            key: 'logout',
            label: (
              <Link href={`/${i18n.language}/logout`}>
                {t('user-logout', 'Logout')}
              </Link>
            ),
          },
        ]),
      }}
    >
      <Trigger>
        {avatar} <span className={'text'}>{profile.firstName}</span>
      </Trigger>
    </Dropdown>
  )
}
